import axios from 'axios';
import { auth } from '@/lib/firebase';
import { Deployment, DeploymentStatus, CreateDeploymentDto } from '@/types/deployment';
import { createAuthHeaders } from '../api-client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const deploymentsApi = {
  async createDeployment(
    projectId: string,
    buildId: string,
    data?: CreateDeploymentDto
  ): Promise<Deployment> {
    const token = await auth.getIdToken();
    const response = await axios.post(
      `${API_URL}/api/projects/${projectId}/builds/${buildId}/deploy`,
      data || {},
      { headers: createAuthHeaders(token!) },
    );
    return response.data;
  },

  async getDeployments(projectId: string, limit = 50, type?: string): Promise<Deployment[]> {
    const token = await auth.getIdToken();
    const params: { limit: number; type?: string } = { limit };
    if (type) {
      params.type = type;
    }
    const response = await axios.get(
      `${API_URL}/api/projects/${projectId}/deploys`,
      { params, headers: createAuthHeaders(token!) },
    );
    return response.data;
  },

  async getDeployment(projectId: string, deployId: string): Promise<Deployment> {
    const token = await auth.getIdToken();
    const response = await axios.get(
      `${API_URL}/api/projects/${projectId}/deploys/${deployId}`,
      { headers: createAuthHeaders(token!) },
    );
    return response.data;
  },

  async getDeploymentStatus(
    projectId: string,
    deployId: string
  ): Promise<DeploymentStatus> {
    const token = await auth.getIdToken();
    const response = await axios.get(
      `${API_URL}/api/projects/${projectId}/deploys/${deployId}/status`,
      { headers: createAuthHeaders(token!) },
    );
    return response.data;
  },

  async getDeploymentLogs(projectId: string, deployId: string): Promise<string> {
    const token = await auth.getIdToken();
    const response = await axios.get(
      `${API_URL}/api/projects/${projectId}/deploys/${deployId}/logs`,
      { headers: createAuthHeaders(token!) },
    );
    return response.data.logs;
  },

  async cancelDeployment(projectId: string, deployId: string): Promise<void> {
    const token = await auth.getIdToken();
    await axios.delete(
      `${API_URL}/api/projects/${projectId}/deploys/${deployId}`,
      { headers: createAuthHeaders(token!) },
    );
  },

  async getCurrentDeployment(projectId: string): Promise<Deployment | null> {
    const token = await auth.getIdToken();
    try {
      const response = await axios.get(
        `${API_URL}/api/projects/${projectId}/deploys/current`,
        { headers: createAuthHeaders(token!) },
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async rollbackToDeployment(projectId: string, deployId: string): Promise<Deployment> {
    const token = await auth.getIdToken();
    const response = await axios.post(
      `${API_URL}/api/projects/${projectId}/deploys/${deployId}/rollback`,
      {},
      { headers: createAuthHeaders(token!) },
    );
    return response.data;
  },
};